import React, { useMemo, useState } from 'react';
import { SmsIcon } from '../components/sms/SmsMarks';
import { OperationItem } from '../types/models';
import { formatDateTime, formatUsd } from '../utils/format';

interface OperationsHistoryProps {
  operations: OperationItem[];
}

const OperationsHistory: React.FC<OperationsHistoryProps> = ({ operations }) => {
  const operationLabels: Record<string, string> = {
    purchase: 'Покупка',
    refund: 'Возврат',
    renewal: 'Продление',
  };
  const [direction, setDirection] = useState<'all' | 'debit' | 'credit'>('all');
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('ru-RU');
    return operations.filter((item) => (direction === 'all' || item.direction === direction)
      && (!needle || `${item.description} ${item.id}`.toLocaleLowerCase('ru-RU').includes(needle)))
      .sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
  }, [operations, direction, query]);
  const resetFilters = () => { setDirection('all'); setQuery(''); };
  return (
    <div className="sms-pageStack sms-history">
      <header className="sms-pageIntro">
        <div><h1>История операций</h1><p>Покупки, продления и возвраты по вашим номерам. Все суммы в USD.</p></div>
      </header>
      <section className="sms-card sms-history__journal" aria-label="Журнал операций">
        <div className="sms-history__toolbar">
          <div className="sms-history__filters" role="group" aria-label="Вид операций">
            {([['all', 'Все операции'], ['debit', 'Списания'], ['credit', 'Возвраты']] as const).map(([value, label]) => (
              <button type="button" key={value} aria-pressed={direction === value} onClick={() => setDirection(value)}>{label}</button>
            ))}
          </div>
          <label className="sms-searchField sms-history__search">
            <span className="sr-only">Поиск операций</span><SmsIcon name="search" />
            <input type="search" className="u2-input" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Описание или ID операции" />
          </label>
        </div>
        <div className="sms-history__count" role="status">Показано {filtered.length} из {operations.length}<span>Сначала новые</span></div>
      {filtered.length > 0 ? <div className="sms-tableWrap">
        <table className="sms-table">
          <caption className="sr-only">Списания и возвраты, сначала новые операции</caption>
          <thead>
            <tr>
              <th scope="col">Операция</th>
              <th scope="col">Дата и время</th>
              <th scope="col">Статус</th>
              <th scope="col" className="sms-history__amount">Сумма</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((item) => (
              <tr key={item.id}>
                <td data-label="Операция">
                  <div className="sms-history__identity">
                    <span className={`sms-history__icon ${item.direction === 'credit' ? 'is-credit' : ''}`} aria-hidden="true">{item.direction === 'credit' ? '↙' : '↗'}</span>
                    <div>
                      <strong>{operationLabels[item.type] || (item.direction === 'credit' ? 'Зачисление' : 'Списание')}</strong>
                      <p>{item.description}</p>
                      <details><summary>Детали операции</summary><span className="sms-history__id">ID: {item.id}</span></details>
                    </div>
                  </div>
                </td>
                <td data-label="Дата и время" className="sms-history__date">{formatDateTime(item.date)}</td>
                <td data-label="Статус">
                  <span className={`sms-status ${item.status === 'done' ? 'is-done' : 'is-pending'}`}>
                    {item.status === 'done' ? 'Выполнено' : item.status === 'pending' ? 'Ожидание' : 'Уточняется'}
                  </span>
                </td>
                <td data-label="Сумма" className={`sms-history__amount ${item.direction === 'credit' ? 'is-positive' : ''}`}>
                  {item.direction === 'credit' ? '+' : '−'}{formatUsd(Math.abs(item.amount))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div> : <div className="sms-history__empty">
        <SmsIcon name={operations.length ? 'search' : 'history'} />
        <h2>{operations.length ? 'Ничего не найдено' : 'Операций пока нет'}</h2>
        <p>{operations.length ? 'Попробуйте другое описание, ID или вид операции.' : 'После покупки номера здесь появятся списания и возвраты.'}</p>
        {operations.length ? <button type="button" className="u2-btn is-secondary is-sm" onClick={resetFilters}>Сбросить фильтры</button> : null}
      </div>}
      </section>
    </div>
  );
};

export default OperationsHistory;
